import { useMemo } from "react";

import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";

import type { MetricSummaryParams, MetricSummaryRow } from "./types";

interface UseMetricSummaryResult {
  rows: MetricSummaryRow[];
  loading: boolean;
  error: string | null;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Normalizes a branch/payor summary response into `{ id, name, value, avg90 }` rows,
 * tolerant of the exact SP shape. Accepts either the SP's object with a single
 * array property (`{ BranchSummaryValues: [...] }` / `{ PayorSummaryValues: [...] }`)
 * or a bare array, and resolves the name/id/value from whichever columns exist.
 */
export function toSummaryRows(data: unknown): MetricSummaryRow[] {
  if (!data || typeof data !== "object") return [];
  let arr: unknown[] = [];
  if (Array.isArray(data)) arr = data;
  else {
    for (const v of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(v)) {
        arr = v;
        break;
      }
    }
  }
  return arr
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
    // The generic SP keeps rows whose code has no lookup match (branch / payor:
    // `IsLookupMatched: false`, named by the bare customer prefix). The old
    // per-dimension SPs dropped them, and there is nothing to name or focus.
   // .filter((r) => r.IsLookupMatched !== false)
    .map((r) => {
      const name =
        r.BranchName ??
        r.PayorTypeName ??
        r.PayorName ??
        r.PayorDesc ??
        r.SalesRep ??
        r.RepName ??
        r.RepFullName ??
        r.ReferralSourceName ??
        r.ReferralSubType ??
        r.DisciplineName ??
        r.Discipline ??
        r.branch_name ??
        r.payor_name ??
        r.BranchCode ??
        // The generic summary (`dashboard/metric-summary`) names its rows the
        // same way whatever it groups by.
        r.GroupName ??
        r.PayorTypeSystemID ??
        r.RepRosterSystemID ??
        r.GroupCode ??
        "";
      const id =
        r.BranchCode ??
        r.BranchSystemID ??
        r.PayorTypeSystemID ??
        r.PayorSystemID ??
        r.RepRosterSystemID ??
        r.RosterSystemID ??
        r.ReferralSourceName ??
        r.ReferralSubType ??
        r.SalesRep ??
        r.Discipline ??
        r.GroupValue ??
        name;
      const value = num(r.MetricValue ?? r.Curr_MetricValue ?? r.Value);
      // Trailing 90-day average — the SPs all emit it under this one name, and
      // it stays null on any that haven't been updated yet.
      const avg90 = num(r.MetricValue90DayAvg);
      return { id: String(id), name: String(name), value, avg90 };
    });
}

/**
 * Branch/payor summary for the drill-down tabs, via POST to `endpoint`. Fetches
 * lazily — pass `params: null` when the tab is not open, so only the active
 * tab's API is called. Re-fires when the selected metric, date range, or filters
 * change (they are part of `params`).
 *
 * Every drill view now calls the one generic summary endpoint, told apart by
 * `summarizeBy` in `params` — so the params alone key each tab's cache entry.
 * `qk.summary` still takes the endpoint so any other caller can't collide.
 *
 * Only the OPEN view is ever enabled; switching back to a tab already visited
 * reads from cache instead of refetching.
 */
export function useMetricSummary(
  endpoint: string,
  params: MetricSummaryParams | null,
): UseMetricSummaryResult {
  const userKey = useUserKey();

  // Callers spread the drill's shared `relationBase` — send only what this SP
  // takes (the API rejects the rest). Keyed on the JSON so a re-created object with the
  // same content doesn't change the body.
  const paramsKey = JSON.stringify(params);
  const body = useMemo<MetricSummaryParams | null>(
    () =>
      params && {
        startDate: params.startDate,
        endDate: params.endDate,
        serviceLineSystemID: params.serviceLineSystemID,
        metricCodes: params.metricCodes,
        filters: params.filters ?? null,
        ...(params.summarizeBy != null ? { summarizeBy: params.summarizeBy } : {}),
        ...(params.summarizeByInterval != null
          ? { summarizeByInterval: params.summarizeByInterval }
          : {}),
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramsKey],
  );

  const { data, loading, error } = useApiQuery<unknown, MetricSummaryRow[]>({
    queryKey: qk.summary(userKey, endpoint, body),
    url: endpoint,
    body,
    // `endpoint` is "" for the Trend view and the not-yet-wired placeholder,
    // which call nothing at all.
    enabled: body != null && endpoint !== "",
    fallbackMessage: "Failed to fetch summary",
    select: toSummaryRows,
  });

  return { rows: data ?? [], loading, error };
}
